// Gallery Box
import Image from 'next/image'

import { useMedia } from 'react-use'

import PrismicLink from '@components/PrismicLink'
import RichTextSerializer from '@components/RichTextSerializer'

interface Props {
	slice: any
	index: number
}

export default function GalleryBox({ slice, index }: Props) {
	const { primary } = slice
	const isHeroPriority = index === 0
	const isDesktop = useMedia('( min-width: 1024px )', false)
	const isMiddlePlacement = primary.content_placement === 'Middle'
	const isMobileImageFitBetween = primary.mobile_image_position === 'Fit between Content'

	// Determine horizontal alignment for box container
	let containerJustifyClass = 'md:justify-start'
	if (isMiddlePlacement) {
		containerJustifyClass = 'md:justify-center'
	} else if (slice.slice_label === 'box_right') {
		containerJustifyClass = 'md:justify-end'
	}

	// Determine flex justify class for box
	let boxJustifyClass = 'justify-between'
	if (isMiddlePlacement) {
		boxJustifyClass = 'justify-center'
	} else if (isMobileImageFitBetween) {
		boxJustifyClass = 'justify-start'
	}

	return (
		<section className="gallery-box section justify-center md:!h-auto md:!min-h-0 !p-0 !max-h-none md:py-16 lg:py-24 !aspect-auto md:!aspect-[13/6] flex-1 min-h-0 !w-full">
			{/* Box container */}
			<div
				className={`gallery-box-container container ${containerJustifyClass} items-center ${isMiddlePlacement ? 'md:items-center' : 'md:items-start'} h-full md:h-auto mb-0`}
			>
				{/* Box */}
				<div
					className={`flex flex-col ${boxJustifyClass} w-full h-full px-6 pt-[88px] pb-[30px] md:p-12 xl:p-16 md:min-h-full md:h-auto ${isMiddlePlacement ? 'md:justify-center' : 'md:justify-start'} safe-top safe-bottom relative z-10 ${
						primary.offset_background_image ? 'max-w-[650px] xl:max-w-[750px]' : 'max-w-[480px]'
					}`}
				>
					{/* Mobile: Title at top, Desktop: Title with description based on subheader_position */}
					{/* A11Y: Text shadow added for contrast over background images */}
					<header
						className={`${isMiddlePlacement ? 'text-center' : 'text-center md:text-left'} pt-safe-top md:pt-0 md:mt-0 min-h-0 relative z-20 flex-shrink-0`}
					>
						{primary.section_kicker?.length > 0 && (
							<div
								className="mb-2 uppercase tracking-wider text-sm md:text-sm leading-[1.1] [&_a]:underline [&_strong]:font-semibold [&_em]:italic"
								style={{
									textShadow: '0 1px 2px rgba(255,255,255,0.9), 0 0 8px rgba(255,255,255,0.5)'
								}}
							>
								<RichTextSerializer content={primary.section_kicker} />
							</div>
						)}

						{primary.section_title?.[0] && (
							<h1
								className="text-4xl lg:text-6xl title mb-3"
								style={{
									textShadow: '0 1px 3px rgba(255,255,255,0.9), 0 0 12px rgba(255,255,255,0.6)'
								}}
							>
								{primary.section_title[0].text}
							</h1>
						)}

						{primary.section_description?.[0] && (
							<h2
								className="md:block mt-2 tracking-wider text-xl leading-[1.1]"
								style={{
									textShadow: '0 1px 2px rgba(255,255,255,0.9), 0 0 8px rgba(255,255,255,0.5)'
								}}
							>
								{primary.section_description[0].text}
							</h2>
						)}
					</header>

					{/* Mobile: Image between content (only when mobile_image_position is "Fit between Content") */}
					{isMobileImageFitBetween &&
						(primary.section_image_mobile?.url || primary.section_image?.url) && (
							<div className="flex justify-center items-center -my-4 lg:hidden flex-grow w-full mx-auto relative z-10 min-h-0">
								<div className="relative w-full h-full max-w-full">
									<Image
										layout="fill"
										objectFit="contain"
										src={primary.section_image_mobile?.url || primary.section_image.url}
										alt={
											primary.section_image_mobile?.alt ||
											primary.section_image.alt ||
											primary.section_image.desktop?.alt ||
											'Hero image'
										}
										priority={isHeroPriority}
									/>
								</div>
							</div>
						)}

					{/* Mobile: CTA at bottom, Desktop: CTA below title */}
					{primary?.section_cta && (
						<div className={`flex justify-center ${isMiddlePlacement ? 'md:justify-center' : 'md:justify-start'} pb-6 md:pb-0 md:mt-8 min-h-0 flex-shrink-0 w-full md:w-auto relative z-20`}>
							<PrismicLink
								link={primary.section_cta}
								label={primary.section_cta_label}
								className={`button button-${primary?.section_cta_color || 'primary'} py-3 px-6 text-lg md:py-4 md:px-8 md:text-lg xl:py-5 xl:px-10 xl:text-xl w-full md:w-auto`}
							/>
						</div>
					)}
				</div>
			</div>
			<div
				className={`${primary.background_color ? '' : 'bg-gray-200'} absolute inset-0 ${
					// eslint-disable-next-line no-nested-ternary
					primary?.offset_background_image && slice.slice_label === 'box_right'
						? 'lg:right-[15%]'
						: primary.offset_background_image
							? 'lg:left-[15%]'
							: ''
				}`}
				style={primary.background_color ? { backgroundColor: primary.background_color } : undefined}
			>
				{/*
					IMAGE SIZING REQUIREMENTS:

					Mobile (section_image.mobile):
					- Viewport: Full viewport height calc(100vh - 103.14px) accounting for navigation
					- Optimal aspect ratio: 2:3 (width:height) - BEST FOR CROSS-DEVICE COMPATIBILITY
					  * This ratio works well across most mobile devices (iPhone, Android, various sizes)
					  * Image uses objectFit="cover" so it will crop to fill viewport
					  * With 2:3 ratio, cropping happens evenly on different devices
					- Recommended dimensions: 1600x2400px (2:3 ratio, 2x retina)
					  * Width: 1600px (covers up to 800px viewport width at 2x retina)
					  * Height: 2400px (maintains 2:3 aspect ratio)
					- Alternative sizes (maintaining 2:3 ratio):
					  * 1200x1800px (smaller file size, still good quality)
					  * 2000x3000px (higher quality for very large displays)
					- Content placement strategy:
					  * Keep important content in the CENTER 50% (both horizontally and vertically)
					  * Avoid placing key elements in:
					    - Top 30%: Title overlay area
					    - Bottom 25%: CTA button overlay area
					    - Outer 15% of left/right edges (may be cropped on wider devices)
					- Image format: WebP (preferred) or JPEG with quality 80-85
					- File size target: Keep under 300KB for optimal loading performance

					Desktop (section_image.desktop):
					- Aspect ratio: 13:6 (landscape orientation)
					- Recommended dimensions: 2600x1200px or higher for retina displays
					- Image positioning: Left or right aligned depending on slice_label
				*/}
				{primary.section_image?.desktop?.url && (
					<div className="relative w-full h-full max-lg:hidden">
						<Image
							layout="fill"
							objectFit="cover"
							src={primary.section_image.desktop.url}
							alt={primary.section_image.desktop.alt || 'Hero background image'}
							priority={isHeroPriority}
						/>
					</div>
				)}
				{/* Mobile background image - only show when mobile_image_position is "Full Background Image" (default) */}
				{primary.section_image?.url && !isMobileImageFitBetween && (
					<div className="relative w-full h-full lg:hidden">
						<Image
							layout="fill"
							objectFit="cover"
							src={primary.section_image_mobile?.url || primary.section_image.url}
							alt={
								primary.section_image_mobile?.alt ||
								primary.section_image.alt ||
								primary.section_image.desktop?.alt ||
								'Hero background image'
							}
							priority={isHeroPriority}
						/>
					</div>
				)}
			</div>
		</section>
	)
}
